import {LoaderFunctionArgs} from 'react-router-dom';
import {MediaTypes, RolesConstant} from '@techsee/techsee-common/lib/constants/account.constants';
import {MeetingMode} from '@techsee/techsee-common/lib/constants/room.constants';
import get from 'lodash/get';
import TsUtils from '@techsee/techsee-common/lib/utils';
import {getRootStore, addErrorEventLog} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {goToDashboard} from '../helper/general.helper';
import {RouteName, RouteNameType} from '../../router/route-names';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';

declare const CLIENT_START_URL: string;

const trace = getAppTracer('join.loader');

function getDashboardRoute(emb: boolean, sw: string | null): RouteNameType {
    return goToDashboard(!!emb, sw as MeetingMode);
}

export async function joinRouteLoader(args: LoaderFunctionArgs): Promise<void> {
    trace.info('Starting');
    const rootStore = getRootStore();
    const url = new URL(args.request.url);
    const params = url.searchParams;
    const nav = rootStore.tsReactStateHelper;

    const roomCode = params.get('roomCode');
    const roomId = params.get('roomId');
    const shortId = params.get('shortId') || params.get('g');
    const observe = params.get('observe');
    const mini = params.get('mini');
    const emb = params.get('emb');
    const sw = params.get('sw');
    const sophieCtx = params.get('sophie-ctx');
    const audio = params.get('audio');

    const chatApi = rootStore.chatApi;
    const userService = rootStore.userService;
    const accountService = rootStore.accountService;
    const roomService = rootStore.roomService;
    const shortUrlService = rootStore.shortUrlService;
    const observationsService = rootStore.observationsService;
    const environmentDetect = rootStore.environmentService;
    const browserUtilsService = rootStore.browserUtilsService;

    const handleJoinError = async (err: any, phase: string, fallbackRoute: RouteNameType): Promise<void> => {
        if (err instanceof Response) throw err;
        const errorMessage = err instanceof Error ? err.message : String(err);
        trace.error(
            `Join loading failed (${phase}): ` + JSON.stringify({isMinidashboard: mini, isEmbeddedDashboard: emb}),
            (err && err.status) || err
        );
        addErrorEventLog(LOG_EVENTS.joinSessionError.type, {error: errorMessage, phase, roomId});
        if (emb) await nav.navigateTo(RouteName.EMB_DASHBOARD_ERROR);
        await nav.navigateTo(fallbackRoute);
    };

    const redirectToRoom = async (
        accountData: any,
        roomIdParam: string,
        currentUser: {userName: string; firstName: string | undefined; lastName: string | undefined; role: string}
    ): Promise<void> => {
        const showRoomId = accountData.protectedSettings ? accountData.protectedSettings.showRoomIdInUrl : true;
        const shouldRedirectToExternalDashboard = get(accountData, 'protectedSettings.v3.enabled');
        const dashboardV3Params = {
            roomId: showRoomId ? roomIdParam : undefined,
            shortId: showRoomId ? undefined : (shortId ?? undefined)
        };

        if (shouldRedirectToExternalDashboard) {
            await rootStore.redirectionService.goToExternalDashboard(dashboardV3Params);

            return; // Page is reloading via window.location.href, stop loader execution
        }

        if (mini && browserUtilsService) {
            browserUtilsService.saveToSessionStorage('miniDashboard', true);
        }
        if (emb && browserUtilsService) {
            browserUtilsService.saveToSessionStorage('embeddedDashboard', true);
        }

        const enableFieldServices = accountData.protectedSettings
            ? accountData.protectedSettings.enableFieldServices
            : true;
        const voiceOverIp = accountData.settings?.fsSettings?.voiceOverIP ?? false;
        const audioEnableInAccount = get(accountData, 'protectedSettings.mediaType') === MediaTypes.videoAndAudio;
        const nWayEnabled = accountData.protectedSettings?.enableNWay;
        const dashboardRoute = getDashboardRoute(!!emb, sw);

        const baseParams: Record<string, any> = {
            room: roomIdParam,
            roomCode: roomCode ?? undefined,
            sw: sw ?? undefined
        };

        if (observe) {
            if (nWayEnabled && sw === MeetingMode.faceMeet.toLowerCase()) {
                await nav.navigateTo(dashboardRoute, {
                    ...baseParams,
                    audio: environmentDetect.isIE11() ? false : audio,
                    observe,
                    'sophie-ctx': sophieCtx
                });
            }

            const filter = {sessionId: '', roomId: ''};
            const isGuest = currentUser.role === RolesConstant.GUEST_OBSERVER;
            const userData: any = {
                userName: currentUser.userName,
                firstName: currentUser.firstName ?? '',
                lastName: currentUser.lastName ?? '',
                addNewSegment: true
            };

            if (showRoomId) {
                userData.roomId = roomIdParam;
                filter.roomId = roomIdParam;
            } else {
                userData.shortId = roomIdParam;
                filter.sessionId = roomIdParam;
            }

            try {
                const response = await userService.joinSessionValidation({
                    id: accountData._id,
                    filter
                });
                if (!response) {
                    throw new Error('joinSessionValidation failed');
                }
                if (!isGuest) {
                    await observationsService.addObservation(userData);
                }
                await nav.navigateTo(dashboardRoute, {
                    ...baseParams,
                    audio: environmentDetect.isIE11() ? false : audio,
                    observe,
                    'sophie-ctx': sophieCtx
                });
            } catch (err: any) {
                await handleJoinError(err, 'redirection', RouteName.INVITE);
            }
        }

        if (environmentDetect.isIE11() || !((enableFieldServices && voiceOverIp) || audioEnableInAccount)) {
            await nav.navigateTo(dashboardRoute, baseParams);
        }

        await nav.navigateTo(dashboardRoute, {
            ...baseParams,
            audio: audio ?? undefined,
            mini: mini ?? undefined,
            emb: emb ?? undefined
        });
    };

    try {
        const user = await userService.getUser('current');
        const currentUser = {
            userName: user.userName,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role
        };

        const accountData = await accountService.getAccount(user.accountId);
        chatApi.ensureSocketIsConnected();

        if (roomId || (shortId && !roomCode)) {
            if (roomId) rootStore.eventLogService.room = roomId;

            const showRoomId = accountData.protectedSettings ? accountData.protectedSettings.showRoomIdInUrl : true;

            try {
                if ((showRoomId && roomId) || (!showRoomId && shortId)) {
                    return await redirectToRoom(
                        accountData,
                        showRoomId ? (roomId as string) : (shortId as string),
                        currentUser
                    );
                }
                if (!showRoomId && !shortId) {
                    const response: any = await roomService.getRoomShortId(roomId as string);
                    const shortIdResult = response?.data?.shortId ?? response?.shortId ?? response ?? '';
                    return await redirectToRoom(accountData, String(shortIdResult), currentUser);
                }
                const data = await shortUrlService.getRoomIdByGuid(shortId as string);
                return await redirectToRoom(accountData, data.roomId, currentUser);
            } catch (err: any) {
                await handleJoinError(err, 'handling', RouteName.INVITE);
            }
        }

        let resolvedRoomId: string;
        const res = await chatApi.requestRoomId(roomCode as string, true);
        if (res?.roomId) {
            resolvedRoomId = res.roomId;
        } else {
            const clientLanguage =
                rootStore.browserUtilsService.getFromSessionStorage('techseeClientLang') ||
                rootStore.tsLocaleHelper.getLocale() ||
                'en_US';
            const room = await roomService.createRoom({
                clientLanguage,
                startUrlCobrowsing: '',
                flowType: 'fs'
            });
            resolvedRoomId = room._id;
        }

        rootStore.eventLogService.room = resolvedRoomId;
        const showRoomId = accountData.protectedSettings ? accountData.protectedSettings.showRoomIdInUrl : true;

        if (showRoomId) {
            return await redirectToRoom(accountData, resolvedRoomId, currentUser);
        }

        const clientStartURL = (TsUtils as any).replaceUrlDomain(CLIENT_START_URL, window.location.hostname);
        const shortUrlData = await shortUrlService.createShortUrl({
            roomId: resolvedRoomId,
            mobileClientURL: clientStartURL
        });
        return await redirectToRoom(accountData, shortUrlData.shortId, currentUser);
    } catch (err: any) {
        await handleJoinError(err, 'general', RouteName.LOGIN);
    }
}
